import { useState, useEffect, useCallback } from 'react';
import { useSse } from '../../sse';
import { getCurrentCounter } from '../../utils/timeCounter';
import { useLeaderboard } from '../../hooks/useLeaderboard';
import { fetchGameStatusStream, startGame, fetchQrCode } from '../../apiservices/axiosservice/gameservice';
import { getSubscriber } from '../../apiservices/LocalStorageService';
import SketcherDrawScreen from './SketcherDrawScreen';
import SketcherPreparingScreen from './SketcherPreparingScreen';
import SketcherGalleryScreen from './SketcherGalleryScreen';
import SketcherGameOver from './SketcherGameOver';
import MusicToggle from './MusicToggle';
import HostGameCode from './HostGameCode';
import './HostSketcherPlay.css';

type ScreenState = 'drawing' | 'preparing' | 'gallery' | 'nosubmission' | 'gameOver';

interface Props {
  onClose: () => void;
  gameId: string;
}

const HostSketcherPlay: React.FC<Props> = ({ onClose, gameId }) => {
  const [screen, setScreen] = useState<ScreenState>('drawing');
  const [roundData, setRoundData] = useState<SketcherCurrentRound | null>(null);
  const [galleryResults, setGalleryResults] = useState<SketcherRoundResult[]>(
    []
  );
  const [galleryExpiryTime, setGalleryExpiryTime] = useState<string | null>(
    null
  );
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentGameStatus, setCurrentGameStatus] = useState<GameStatusInfo>();
  const [zeroSketchData, setZeroSketchData] = useState<ZeroSketchReport | null>(null);
  const [startingGame, setStartingGame] = useState(false);
  const [qrCode, setQrCode] = useState<string>('');
  const leaderboard = useLeaderboard(gameId);
  const user = getSubscriber();

  const handleStartGame = useCallback(async () => {
    const request: PreviewRemoteRequest = {
      gameId,
      gameOwnerId: user?.userId,
    };

    setStartingGame(true);
    try {
      await startGame(request);
      setScreen('preparing');
    } catch (err) {
      console.log('Error starting game:', err);
    } finally {
      setStartingGame(false);
    }
  }, [gameId, user?.userId]);

  // SSE subscriptions
  const sketcherRound = useSse<SketcherCurrentRound>('sketcher-current-round');
  const sketcherResult = useSse<SketcherResultsWrapper>(
    'sketcher-round-result'
  );
  const gameStatus = useSse<GameStatusInfo>('game-status');
  const zeroSketchReport = useSse<ZeroSketchReport>('zero-sketch-report');

  // Fetch game status on mount
  useEffect(() => {
    fetchGameStatusStream(gameId)
      .then((res) => {
        setCurrentGameStatus(res.data);
        if (res.data.status === 'FINISHED') {
          setScreen('gameOver');
        }
      })
      .catch((error) => {
        console.log('Error fetching game status:', error);
      });
  }, [gameId]);

  // Fetch the join QR once per game (not on every screen switch)
  useEffect(() => {
    if (!gameId) return;
    fetchQrCode(gameId)
      .then((res) => setQrCode(res.data.qrCodeBase64Image))
      .catch((error) => console.log('Error fetching QR code:', error));
  }, [gameId]);

  // SSE: game status updates
  useEffect(() => {
    if (gameStatus) {
      setCurrentGameStatus(gameStatus);
      if (gameStatus.status === 'FINISHED') {
        setScreen('gameOver');
      }
    }
  }, [gameStatus]);

  // SSE: new round received → show draw screen
  useEffect(() => {
    if (sketcherRound) {
      setRoundData(sketcherRound);
      setTimeLeft(getCurrentCounter(sketcherRound.currentExpiryTime));
      setGalleryResults([]);
      setGalleryExpiryTime(null);
      setScreen('drawing');
    }
  }, [sketcherRound]);

  // SSE: round result received → show gallery.
  // Each event is displayed on its own: a new event REPLACES the previous
  // results (no cross-event accumulation). Whatever records arrived in the
  // latest event are shown together.
  useEffect(() => {
    if (sketcherResult?.sketcherBatchResults) {
      setGalleryResults(sketcherResult.sketcherBatchResults);
      setGalleryExpiryTime(sketcherResult.currentExpiryTime ?? null);
      setScreen('gallery');
    }
  }, [sketcherResult]);

  // SSE: zero sketch report — no submissions received for the round
  useEffect(() => {
    if (zeroSketchReport) {
      setZeroSketchData(zeroSketchReport);
      setScreen('nosubmission');
    }
  }, [zeroSketchReport]);

  // Timer countdown (only transitions to preparing from the drawing screen)
  useEffect(() => {
    if (timeLeft <= 0 || screen !== 'drawing') return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          setScreen('preparing');
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft, screen]);

  // Screens
  switch (screen) {
    case 'drawing':
      if (!roundData) {
        return (
          <div className="host-sketcher">
            <MusicToggle />
            <HostGameCode gameId={gameId} qrCode={qrCode} />
            <div className="sketcher-waiting">
              <div className="sketcher-waiting__content">
                <div className="sketcher-waiting__animation">
                  <span className="sketcher-waiting__emoji">🎨</span>
                  <div className="sketcher-waiting__dots">
                    <span className="sketcher-waiting__dot" />
                    <span className="sketcher-waiting__dot" />
                    <span className="sketcher-waiting__dot" />
                  </div>
                </div>
                <h2 className="sketcher-waiting__title">Game Not Yet Received</h2>
                <p className="sketcher-waiting__subtitle">
                  The round data hasn't arrived yet. Tap below to start the game.
                </p>
                <button
                  className="sketcher-waiting__btn"
                  onClick={handleStartGame}
                  disabled={startingGame}
                >
                  {startingGame ? 'Starting...' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="host-sketcher">
          <MusicToggle />
          <HostGameCode gameId={gameId} qrCode={qrCode} />
          <SketcherDrawScreen
            sketchItem={roundData.sketchItem}
            hint={roundData.hint}
            roundNumber={roundData.roundNumber}
            totalRounds={roundData.totalRounds}
            timeLeft={timeLeft}
          />
        </div>
      );

    case 'preparing':
      return (
        <div className="host-sketcher">
          <MusicToggle />
          <HostGameCode gameId={gameId} qrCode={qrCode} />
          <SketcherPreparingScreen
            roundNumber={roundData?.roundNumber || 1}
            totalRounds={roundData?.totalRounds || 1}
          />
        </div>
      );

    case 'gallery':
      return (
        <div className="host-sketcher">
          <MusicToggle />
          <HostGameCode gameId={gameId} qrCode={qrCode} />
          <SketcherGalleryScreen
            results={galleryResults}
            roundNumber={roundData?.roundNumber || 1}
            totalRounds={roundData?.totalRounds || 1}
            onNext={handleStartGame}
            expiryTime={galleryExpiryTime}
          />
        </div>
      );

    case 'nosubmission':
      return (
        <div className="host-sketcher">
          <MusicToggle />
          <HostGameCode gameId={gameId} qrCode={qrCode} />
          <div className="sketcher-waiting">
            <div className="sketcher-waiting__content">
              <div className="sketcher-waiting__animation">
                <span className="sketcher-waiting__emoji">🚫</span>
                <div className="sketcher-waiting__dots">
                  <span className="sketcher-waiting__dot" />
                  <span className="sketcher-waiting__dot" />
                  <span className="sketcher-waiting__dot" />
                </div>
              </div>
              <h2 className="sketcher-waiting__title">No Sketch Submissions</h2>
              <p className="sketcher-waiting__subtitle">
                No players submitted a sketch this round.
              </p>
              <span className="sketcher-waiting__round">
                Round {zeroSketchData?.currentRound || 1}/{zeroSketchData?.totalRounds || 1}
              </span>
              <button
                className="sketcher-waiting__btn"
                onClick={handleStartGame}
                disabled={startingGame}
              >
                {startingGame ? 'Starting...' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      );

    case 'gameOver':
      return (
        <SketcherGameOver
          onClose={onClose}
          leaderboard={leaderboard}
          gameInfo={currentGameStatus}
        />
      );

    default:
      return null;
  }
};

export default HostSketcherPlay;
